import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Toolbar,
  Tooltip,
  Chip,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Alert,
  TablePagination,
  Fab,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import { schemasAPI, dataAPI } from '../../services/api';
import DataRecordDialog from './DataRecordDialog';

const SchemaRecordsDialog = ({ open, onClose, schema, onRecordsUpdate }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [recordDialogOpen, setRecordDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    search: ''
  });

  useEffect(() => {
    if (open && schema) {
      fetchRecords();
    }
  }, [open, schema, page, rowsPerPage, filters]);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...filters
      };

      const response = await schemasAPI.getSchemaRecords(schema._id, params);
      setRecords(response.data.records);
      setTotalRecords(response.data.pagination.total);
    } catch (error) {
      console.error('Error fetching records:', error);
      toast.error('Failed to load records');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuClick = (event, record) => {
    setMenuAnchor(event.currentTarget);
    setSelectedRecord(record);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedRecord(null);
  };

  const handleAddRecord = () => {
    setEditingRecord(null);
    setRecordDialogOpen(true);
  };

  const handleEditRecord = (record) => {
    setEditingRecord(record);
    setRecordDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteRecord = async (record) => {
    try {
      await dataAPI.deleteRecord(record._id);
      toast.success('Record deleted successfully');
      fetchRecords();
      onRecordsUpdate?.();
      handleMenuClose();
    } catch (error) {
      console.error('Error deleting record:', error);
      toast.error('Failed to delete record');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRecords.length === 0) return;

    try {
      await dataAPI.bulkDeleteRecords(selectedRecords);
      toast.success(`${selectedRecords.length} records deleted successfully`);
      setSelectedRecords([]);
      fetchRecords();
      onRecordsUpdate?.();
    } catch (error) {
      console.error('Error bulk deleting records:', error);
      toast.error('Failed to delete records');
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedRecords(records.map(record => record._id));
    } else {
      setSelectedRecords([]);
    }
  };

  const handleSelectRecord = (recordId) => {
    if (selectedRecords.includes(recordId)) {
      setSelectedRecords(selectedRecords.filter(id => id !== recordId));
    } else {
      setSelectedRecords([...selectedRecords, recordId]);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return 'success';
      case 'draft': return 'warning';
      case 'archived': return 'default';
      default: return 'default';
    }
  };

  const formatFieldValue = (value, fieldType) => {
    if (value === null || value === undefined) return '-';
    
    switch (fieldType) {
      case 'boolean':
        return value ? 'Yes' : 'No';
      case 'date':
        return new Date(value).toLocaleDateString();
      case 'array':
        return Array.isArray(value) ? `[${value.length} items]` : value;
      case 'object_array':
        if (Array.isArray(value)) {
          if (value.length === 0) return 'Empty array';
          if (value.length === 1) return '1 object';
          return `${value.length} objects`;
        }
        return Array.isArray(value) ? `[${value.length} objects]` : value;
      case 'object':
        return typeof value === 'object' ? '[Object]' : value;
      default:
        return String(value);
    }
  };

  const exportRecords = () => {
    const csvContent = [
      // Header
      ['ID', 'Status', 'Created', ...schema.fields.map(field => field.label)].join(','),
      // Data rows
      ...records.map(record => [
        record._id,
        record.status,
        new Date(record.createdAt).toLocaleDateString(),
        ...schema.fields.map(field => {
          const value = record.data[field.name];
          return `"${formatFieldValue(value, field.type).replace(/"/g, '""')}"`;
        })
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${schema.slug}-records.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!schema) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        fullScreen={{ xs: true, sm: false }}
        PaperProps={{
          sx: { 
            height: { xs: '100vh', sm: '90vh' },
            m: { xs: 0, sm: 3 }
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">
                {schema.name} - Data Records
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {totalRecords} total records
              </Typography>
            </Box>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* Toolbar */}
          <Box sx={{ mb: 3 }}>
            <Toolbar 
              sx={{ 
                pl: { xs: 1, sm: 2 }, 
                pr: { xs: 1, sm: 1 },
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 2, sm: 0 },
                minHeight: { xs: 'auto', sm: '64px' },
                py: { xs: 2, sm: 1 },
                ...(selectedRecords.length > 0 && {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText'
                })
              }}
            >
              {selectedRecords.length > 0 ? (
                <>
                  <Typography sx={{ flex: '1 1 100%', textAlign: { xs: 'center', sm: 'left' } }} variant="subtitle1">
                    {selectedRecords.length} selected
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-end' } }}>
                    <Tooltip title="Delete selected">
                      <IconButton onClick={handleBulkDelete} sx={{ color: 'inherit' }}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </>
              ) : (
                <>
                  <Typography 
                    sx={{ 
                      flex: '1 1 100%', 
                      textAlign: { xs: 'center', sm: 'left' },
                      mb: { xs: 0, sm: 0 }
                    }} 
                    variant="h6"
                  >
                    Records
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 1,
                    width: { xs: '100%', sm: 'auto' }
                  }}>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleAddRecord}
                      size="small"
                      fullWidth={{ xs: true, sm: false }}
                    >
                      Add Record
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={exportRecords}
                      size="small"
                      fullWidth={{ xs: true, sm: false }}
                    >
                      Export CSV
                    </Button>
                  </Box>
                </>
              )}
            </Toolbar>

            {/* Filters */}
            <Grid container spacing={2} sx={{ mt: 1, px: { xs: 2, sm: 0 } }}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="">All Status</MenuItem>
                    <MenuItem value="published">Published</MenuItem>
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="archived">Archived</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search records"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Records Table */}
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
              <CircularProgress />
            </Box>
          ) : records.length === 0 ? (
            <Alert severity="info">
              No records found. {filters.status || filters.search ? 'Try adjusting your filters.' : 'Add your first record to get started.'}
            </Alert>
          ) : (
            <>
              {/* Mobile Card View */}
              <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                {records.map((record) => (
                  <Card key={record._id} sx={{ mb: 2 }}>
                    <CardContent>
                      {/* Header with checkbox and status */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Checkbox
                            checked={selectedRecords.includes(record._id)}
                            onChange={() => handleSelectRecord(record._id)}
                          />
                          <Chip 
                            label={record.status} 
                            color={getStatusColor(record.status)}
                            size="small"
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" color="textSecondary">
                            {new Date(record.createdAt).toLocaleDateString()}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuClick(e, record)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Box>
                      </Box>
                      
                      {/* Data fields */}
                      <Box>
                        {schema.fields.slice(0, 3).map((field) => (
                          <Box key={field.name} sx={{ mb: 1 }}>
                            <Typography variant="caption" color="textSecondary">
                              {field.label}:
                            </Typography>
                            <Typography variant="body2" sx={{ ml: 1 }}>
                              {formatFieldValue(record.data[field.name], field.type)}
                            </Typography>
                          </Box>
                        ))}
                        {schema.fields.length > 3 && (
                          <Typography variant="caption" color="textSecondary">
                            +{schema.fields.length - 3} more fields
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>

              {/* Desktop Table View */}
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            indeterminate={selectedRecords.length > 0 && selectedRecords.length < records.length}
                            checked={records.length > 0 && selectedRecords.length === records.length}
                            onChange={handleSelectAll}
                          />
                        </TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Created</TableCell>
                        {schema.fields.slice(0, 4).map((field) => (
                          <TableCell key={field.name}>
                            {field.label}
                          </TableCell>
                        ))}
                        {schema.fields.length > 4 && (
                          <TableCell>...</TableCell>
                        )}
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {records.map((record) => (
                        <TableRow 
                          key={record._id}
                          selected={selectedRecords.includes(record._id)}
                          hover
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedRecords.includes(record._id)}
                              onChange={() => handleSelectRecord(record._id)}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={record.status} 
                              color={getStatusColor(record.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {new Date(record.createdAt).toLocaleDateString()}
                          </TableCell>
                          {schema.fields.slice(0, 4).map((field) => (
                            <TableCell key={field.name}>
                              {formatFieldValue(record.data[field.name], field.type)}
                            </TableCell>
                          ))}
                          {schema.fields.length > 4 && (
                            <TableCell>
                              <Typography variant="body2" color="textSecondary">
                                +{schema.fields.length - 4} more
                              </Typography>
                            </TableCell>
                          )}
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuClick(e, record)}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </>
          )}

          {/* Pagination */}
          {!loading && records.length > 0 && (
            <TablePagination
              component="div"
              count={totalRecords}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                '& .MuiTablePagination-toolbar': {
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 1, sm: 0 }
                },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }
              }}
            />
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleEditRecord(selectedRecord)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Record</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleDeleteRecord(selectedRecord)}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete Record</ListItemText>
        </MenuItem>
      </Menu>

      {/* Data Record Dialog */}
      {recordDialogOpen && (
        <DataRecordDialog
          open={recordDialogOpen}
          onClose={() => setRecordDialogOpen(false)}
          schema={schema}
          record={editingRecord}
          onSave={() => {
            fetchRecords();
            onRecordsUpdate?.();
          }}
        />
      )}
    </>
  );
};

export default SchemaRecordsDialog; 